/**
 * ChatRoute — the `/chat` surface (STORY-002-009 step 1).
 *
 * Composition:
 *   <ThreadList/>         full chat-history rail (STORY-002-010)
 *   header                assistant title + online dot (prototype top bar)
 *   <MessageList/>        persisted messages + streaming reveal + error slots
 *   <MessageInput/>       composer; Send / Stop
 *
 * Streaming wiring:
 *   - `useChatStream` drives the fetch + ReadableStream transport.
 *   - Its typed callbacks update the `useChatStore` (intent/token/error/done).
 *   - On `done` we invalidate the thread query so the next render reads the
 *     committed message from TanStack Query's cache (AC #4).
 *   - Most errors surface BOTH in the message slot (MessageList) AND a
 *     Sonner toast (AC #5/#13). `rate_limited_user`/`rate_limited_ip` are the
 *     one deliberate exception (bug-wave 2026-09-02, see `toastForError`
 *     below) — slot only, to kill the duplicated messaging QA evidenced.
 *
 * Auth: members reach it through ChatEntryRoute (/chat); anonymous visitors
 * in guest mode get it at `/` through HomeRoute with `guestConfig`
 * (SCRUM-273/275); with guest mode off ChatEntryRoute keeps AC #12's
 * /login?return_to=/chat redirect.
 *
 * Language (AC #8): the active output language is the current i18n language
 * (RU/KY/EN — the server uses it for prompt selection). The user's persisted
 * `preferred_language` becomes the i18n language once the account/me wiring
 * lands; here the live i18n language is the single source of truth.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PanelLeft } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { notify } from "@/lib/notify";

import { ApiError, apiClient } from "@/lib/api-client";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/i18n";
import { withReturnTo } from "@/features/auth/destination";
import { useChatStore, type ChatStreamError } from "@/features/chat/chat-store";
import type { MessageAttachment } from "@/features/chat/attachment-types";
import {
  GUEST_ME_QUERY_KEY,
  fetchGuestMe,
  rebootstrapAfterGuestEnded,
  syncPointerFromMe,
  type GuestConfig,
} from "@/features/chat/guest/api";
import {
  isGuestStreamErrorCode,
  stopCauseFor,
  stopCauseForAllowance,
  type GuestAllowance,
  type GuestGate,
  type GuestStopCause,
} from "@/features/chat/guest/allowance";
import {
  clearGoneThread,
  useGuestClaim,
  useGuestClaimStore,
} from "@/features/chat/guest/GuestClaimController";
import { GuestHistoryPanel } from "@/features/chat/guest/GuestHistoryPanel";
import { GuestComposerNotice, GuestGateCard } from "@/features/chat/guest/GuestGateCard";
import { GuestGateSheet } from "@/features/chat/guest/GuestGateSheet";
import { GuestSavePrompt } from "@/features/chat/guest/GuestSavePrompt";
import {
  clearGuestDraft,
  clearGuestPointer,
  guestReturnTo,
  readGuestDraft,
  readGuestPointer,
  takeGuestSessionExpired,
  updateGuestPointer,
  useGuestPointer,
  writeGuestDraft,
  writeGuestPointer,
} from "@/features/chat/guest/pointer";
import {
  selectAuthBootstrapped,
  selectGuestToken,
  selectIsAuthenticated,
  useAuthStore,
} from "@/lib/auth-store";
import {
  MessageInput,
  type MessageInputHandle,
} from "@/features/chat/MessageInput";
import {
  isConversationEmpty,
  MessageList,
  type ThreadMessage,
} from "@/features/chat/MessageList";
import { NeedSeeAllSheet } from "@/features/chat/components/NeedSeeAllSheet";
import {
  collectPlanNeeds,
  deriveExploredNeedIds,
} from "@/features/chat/plan-rehydrate";
import { AssistantFrame } from "@/features/chat/AssistantFrame";
import { ThreadList } from "@/features/chat/ThreadList";
import { threadListQueryKey } from "@/features/chat/useThreadList";
import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { dismissClarifyRound } from "@/features/chat/clarify/api";
import { withRoundStatus } from "@/features/chat/clarify/cached-round";
import { ClarifyCard } from "@/features/chat/clarify/ClarifyCard";
import { draftFor } from "@/features/chat/clarify/draft";
import { roundMayDock, selectOpenRound } from "@/features/chat/clarify/open-round";
import { buildClarifySummary } from "@/features/chat/clarify/summary";
import type {
  ClarifyAnswerInput,
  ClarifyRoundEvent,
  ClarifyRoundOption,
  ClarifyStatus,
} from "@/features/chat/clarify/types";
import {
  useChatStream,
  type Need,
  type ProductCardData,
  type StreamErrorEvent,
} from "@/features/chat/useChatStream";

import { Button } from "@/components/ui/button";

/** Mirrors backend `ThreadDetailResponse` (STORY-002-005). */
interface ThreadDetailResponse {
  thread: { id: string; title: string | null; language: string };
  messages: ThreadMessage[];
  /** SCRUM-177 — older messages exist beyond the requested window. */
  has_more?: boolean;
}

/**
 * SCRUM-177 — how many messages to pull when opening a thread.
 *
 * The endpoint used to serve the repository default of 20 with no way to ask
 * for more, so reopening a long conversation showed only its tail (live: a
 * 50-message thread rendered 20). The API now takes `limit` and returns
 * `has_more`; this window covers any realistic conversation in one request.
 * `has_more` is surfaced on the response for the "load earlier" control
 * (`MessageList.tsx`'s `hasEarlierMessages`/`onLoadEarlier`) — a thread
 * longer than `THREAD_MESSAGE_MAX_LIMIT` below still truncates; cursor
 * pagination on the detail endpoint is the honest fix and is out of scope
 * here (B3, bug wave 2026-09-02).
 */
const THREAD_MESSAGE_LIMIT = 200;

/** How much further back each "load earlier" tap reaches (SCRUM-177). */
const THREAD_MESSAGE_STEP = 200;

/**
 * B3 (SCRUM-177 residual) — mirrors `router_threads.py::_THREAD_MESSAGE_MAX_LIMIT`.
 * The OpenAPI schema carries this bound (`le=500`) but the shared-types
 * codegen emits no runtime constant, so it is duplicated here with this
 * cross-reference. Without the clamp, stepping past it (200 → 400 → 600)
 * 422s on the third "load earlier" click.
 */
const THREAD_MESSAGE_MAX_LIMIT = 500;

function threadQueryKey(threadId: string | null): readonly unknown[] {
  return ["chat", "thread", threadId];
}

/**
 * Index of the thread id inside the FULL thread-detail query key
 * (`[...threadQueryKey(id), messageLimit]` → `["chat", "thread", id,
 * messageLimit]`). Named so a future change to `threadQueryKey`'s shape
 * fails loudly here instead of silently mis-scoping the B1 `placeholderData`
 * comparison below.
 */
const THREAD_KEY_ID_INDEX = 2;

/**
 * W2.6 fix round — whether a guest write to the pointer that a stream brings
 * back (the allowance frame, the adoption on `done`, a refusal's allowance)
 * may land: never in a tab that holds a member token, and never to resurrect
 * a pointer that a claim or discard — in this tab or another — cleared while
 * the turn was on the wire (the same guard as the `/auth/guest/me` sync). A
 * pointer re-created with `hasSession: true` put the member's tab on a
 * spurious «Сохранить разговор?» after a successful claim.
 */
function guestPointerWritable(): boolean {
  return useAuthStore.getState().accessToken === null && readGuestPointer() !== null;
}

/**
 * W3.2 fix round — is focus nowhere, or inside the composer that the render
 * calling this is about to unmount (`root` holds it)? Then the browser drops
 * focus to `<body>` once it goes, and the slot's next occupant should take it.
 * The composer is located by its test id, as `findScrollEl` locates the list.
 */
function focusLeavesWithComposer(root: HTMLElement | null): boolean {
  const active = document.activeElement;
  if (active === null || active === document.body) return true;
  return root?.querySelector('[data-testid="chat-composer"]')?.contains(active) ?? false;
}

/** Narrow the live i18n language to a supported output language. */
function resolveLanguage(raw: string): SupportedLanguage {
  const base = raw.split("-")[0] ?? "ru";
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as SupportedLanguage)
    : "ru";
}

/**
 * A0: extract a carried composer seed from the router `location.state`. A
 * "Ask the assistant about this" link (a seller / KB article) navigates to
 * `/chat` with `state: { seed }`; the composer prefills it (without auto-send).
 */
function readSeed(state: unknown): string {
  if (
    typeof state === "object" &&
    state !== null &&
    "seed" in state &&
    typeof (state as { seed: unknown }).seed === "string"
  ) {
    return (state as { seed: string }).seed;
  }
  return "";
}

/** SCRUM-179 — the query param that pins the open thread across a reload. */
const THREAD_PARAM = "t";

export interface ChatRouteProps {
  /**
   * Guest mode (SCRUM-273): the public config from GET /auth/guest/config,
   * passed by HomeRoute for an anonymous visitor at `/`. Absent for members —
   * the route then behaves exactly as before.
   */
  guestConfig?: GuestConfig;
}

export function ChatRoute({ guestConfig }: ChatRouteProps = {}): ReactElement {
  const { t, i18n } = useTranslation("chat");
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  // A0: a composer seed carried from a "ask the assistant about this" link.
  const seedText = readSeed(location.state);

  // Guest mode (SCRUM-273): `guestConfig` comes from HomeRoute; absent for members.
  const guest = guestConfig !== undefined;
  const attachmentsLocked = guestConfig !== undefined && !guestConfig.attachments_enabled;
  const pointer = useGuestPointer();
  const guestToken = useAuthStore(selectGuestToken);
  const bootstrapped = useAuthStore(selectAuthBootstrapped);
  const isMember = useAuthStore(selectIsAuthenticated);
  const guestAllowance: GuestAllowance | null = guest ? (pointer?.allowance ?? null) : null;
  /** Why the sign-up sheet is open (W2.5 renders it), or null. */
  const [gate, setGate] = useState<GuestGate | null>(null);
  /**
   * The sign-up sheet a turn's allowance asked for, held back while that
   * turn's clarify or quote round holds the composer's slot (final fix wave,
   * I4): answering is free at the wall, and the sheet would cover the round.
   * It opens once no round is docked and no answer streams — the answer's
   * `done`, or ✕.
   */
  const [heldGate, setHeldGate] = useState<GuestGate | null>(null);
  /** A stop-cold refusal (limit, budget, claimed, disabled) from the last send. */
  const [guestStop, setGuestStop] = useState<GuestStopCause | null>(null);
  /** The in-flight turn's `allowance` frame, acted on at `done`. */
  const pendingAllowanceRef = useRef<GuestAllowance | null>(null);
  /** The composer becomes the sign-up card (W2.5) for this cause, or null. */
  const guestStopCause: GuestStopCause | null =
    guestConfig === undefined
      ? null
      : (guestStop ??
        (!guestConfig.enabled
          ? "disabled"
          : guestAllowance !== null && guestAllowance.state === "exhausted"
            ? stopCauseForAllowance(guestAllowance)
            : null));
  /**
   * `guestStopCause` as the `fetch_need` gate reads it (`doFetchNeed`,
   * SCRUM-274). A ref, because the "show me everything" loop keeps calling the
   * `doFetchNeed` of the render it started in; a tap the server refused writes
   * it at once, so the loop's next step opens the sheet instead of asking the
   * server again before React has re-rendered.
   */
  const guestStopCauseRef = useRef<GuestStopCause | null>(guestStopCause);
  guestStopCauseRef.current = guestStopCause;
  // Fix round (Important): focus must not drop to <body> when the allowance
  // frame's re-render swaps MessageInput (which may hold focus — the
  // textarea, or the just-clicked send button) for GuestGateCard. Only
  // steal focus for a TRANSITION into the card during THIS session — never
  // on a cold load (an already-exhausted allowance restored from the
  // pointer, or guest mode already off on the very first render), where
  // nothing had focus to begin with and grabbing it would be a surprise
  // auto-focus. The condition deliberately does not look at `gate` at all:
  // the server may send `exhausted` with `show_prompt: false`, where the
  // card appears with no sheet, and it must still take focus.
  // `previousGuestStopCauseRef`'s initializer reads THIS render's value, so
  // a cold load's first render always computes `false` below (previous ===
  // current); only a LATER render, where the ref still holds an EARLIER
  // commit's null, computes `true`.
  const previousGuestStopCauseRef = useRef<GuestStopCause | null>(guestStopCause);
  const guestGateCardFocusOnMount =
    guestStopCause !== null && previousGuestStopCauseRef.current === null;
  useEffect(() => {
    previousGuestStopCauseRef.current = guestStopCause;
  }, [guestStopCause]);
  // W2.6 — a member with a guest conversation still in this browser: until
  // it is claimed or dropped, `?t=` may name the GUEST's thread, which this
  // member cannot read yet (spec: "claimPending is computed from the pointer
  // at render, so ChatRoute never fetches the guest thread as the member
  // first"). GuestClaimController decides; GuestSavePrompt shows it.
  const claimPending = !guest && pointer !== null && pointer.hasSession;
  const { phase: claimPhase, save: saveGuestClaim, dontSave: dropGuestClaim } = useGuestClaim();
  /** The thread a "gone" claim opened (P10) — worded as expired if it 404s. */
  const goneThreadId = useGuestClaimStore((s) => s.goneThreadId);
  /** Where every guest sign-in entry returns to (contract: `/chat?t=<id>`). */
  const guestReturn = guestReturnTo(pointer);
  /** The optional "N left" hint: only when the admin switched it on (contract:
   * no prompt before the limit unless `warn_at_remaining > 0` AND the
   * allowance is already in "warn"). Soft mode keeps "warn" past the limit
   * with `remaining: 0` — the guest keeps writing — so "0 left" would sit
   * under the composer for good; it is hidden there (final web fix round,
   * item 6). */
  const remainingHint =
    guestConfig !== undefined &&
    guestConfig.warn_at_remaining > 0 &&
    guestAllowance !== null &&
    guestAllowance.state === "warn" &&
    !(guestAllowance.soft_mode && guestAllowance.remaining === 0)
      ? guestAllowance.remaining
      : null;

  const [searchParams, setSearchParams] = useSearchParams();
  // SCRUM-177 — the message window, widened by "load earlier". Reset whenever
  // a different thread opens so switching threads doesn't inherit a huge fetch.
  const [messageLimit, setMessageLimit] = useState(THREAD_MESSAGE_LIMIT);

  const activeThreadId = useChatStore((s) => s.activeThreadId);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const routingIntent = useChatStore((s) => s.routingIntent);
  const currentAssistantText = useChatStore((s) => s.currentAssistantText);
  const currentProductCards = useChatStore((s) => s.currentProductCards);
  const currentSellerCards = useChatStore((s) => s.currentSellerCards);
  const currentCitations = useChatStore((s) => s.currentCitations);
  const currentFollowups = useChatStore((s) => s.currentFollowups);
  const currentStage = useChatStore((s) => s.currentStage);
  const awaitingFirstFrameSince = useChatStore((s) => s.awaitingFirstFrameSince);
  const currentMaterials = useChatStore((s) => s.currentMaterials);
  const currentPlan = useChatStore((s) => s.currentPlan);
  const currentClarify = useChatStore((s) => s.currentClarify);
  const exploredNeedIds = useChatStore((s) => s.exploredNeedIds);
  const needFetchStatus = useChatStore((s) => s.needFetchStatus);
  const streamError = useChatStore((s) => s.streamError);
  const localTurns = useChatStore((s) => s.localTurns);
  const scrollPositions = useChatStore((s) => s.scrollPositions);
  const setActiveThreadId = useChatStore((s) => s.setActiveThreadId);
  const localThreadId = useChatStore((s) => s.localThreadId);
  const adoptServerThread = useChatStore((s) => s.adoptServerThread);
  const closedClarifyRounds = useChatStore((s) => s.closedClarifyRounds);
  const clarifyDraft = useChatStore((s) => s.clarifyDraft);

  // SCRUM-179 — survive a reload. `activeThreadId` lives in the zustand
  // store, which carries no persistence middleware. That the store as a
  // whole is memory-only is a general design choice; REQ-PRD-§11.2-A itself
  // is narrower — it bans only the access token and PII from localStorage,
  // not every field this store happens to hold. Either way, a browser
  // refresh reset `activeThreadId` to null and the user landed in a
  // brand-new empty chat, losing the conversation they were reading.
  //
  // The URL is the right home for "which thread am I looking at": it survives
  // reload, restores with back/forward, and makes a thread shareable — without
  // storing anything. The store stays the single source of truth; `?t=` is a
  // mirror written with `replace` so switching threads doesn't spam history.
  const urlThreadId = searchParams.get(THREAD_PARAM);

  // B1 (SCRUM-177/179 residual) — the thread this render should treat as
  // "open", resolved SYNCHRONOUSLY rather than waiting for the restore effect
  // below. On a cold load with `?t=` present, first paint has
  // `activeThreadId === null` (the store hasn't caught up yet) — without this
  // fallback the thread query stayed `enabled: false` for that whole first
  // commit, which is exactly the one-frame gap that let the empty state flash
  // before the real transcript ever started loading. The store remains the
  // single source of truth once the restore effect runs; this is a
  // render-time fallback only, and does not change which effect owns writing
  // `?t=` (`handleNewChat` still owns clearing it).
  const openThreadId = activeThreadId ?? urlThreadId;

  // W2.6 — a thread the URL may still name that THIS identity must not read:
  // the guest's thread once the member chose «Не сохранять», and the previous
  // identity's thread after a store reset in place (the 409 claim outcome, a
  // session end). React Router applies a navigation in a transition, so the
  // render right after the pointer clears or the store resets still sees the
  // old `?t=` — without this, `fetchThread` would fetch it as the member in
  // exactly that render. A ref, not state: it must already hold in that
  // render, whichever lane React runs it in. It only covers the render-time
  // URL fallback (`activeThreadId === null`): opening that thread explicitly
  // (the sidebar, adoption) is the current identity's own choice. Released
  // once the URL moves.
  const staleThreadIdRef = useRef<string | null>(null);
  const urlThreadIdRef = useRef(urlThreadId);
  urlThreadIdRef.current = urlThreadId;
  useEffect(() => {
    if (urlThreadId !== staleThreadIdRef.current) staleThreadIdRef.current = null;
  }, [urlThreadId]);

  // Restore on mount / when the param changes underneath us (back button).
  useEffect(() => {
    if (urlThreadId !== null && urlThreadId !== activeThreadId) {
      setActiveThreadId(urlThreadId);
    }
    // Only react to the URL side here; the store->URL direction is below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlThreadId]);

  // Mirror the store back into the URL when a thread is open.
  //
  // This effect only ever WRITES a thread id, never deletes one. Both effects
  // run in the same commit, so on a cold load with `?t=` present this one still
  // sees `activeThreadId === null` (the restore above has been scheduled but
  // not applied) — a delete here would wipe the very param we are restoring
  // from, which is exactly what it did before this guard. Clearing is instead
  // owned by `handleNewChat`, the only place that deliberately drops the thread.
  useEffect(() => {
    if (activeThreadId === null || activeThreadId === urlThreadId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(THREAD_PARAM, activeThreadId);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId]);
  const setScrollPosition = useChatStore((s) => s.setScrollPosition);
  const pushUserTurn = useChatStore((s) => s.pushUserTurn);
  const beginStream = useChatStore((s) => s.beginStream);
  const markStreamFrame = useChatStore((s) => s.markStreamFrame);
  const setRoutingIntent = useChatStore((s) => s.setRoutingIntent);
  const setProductCards = useChatStore((s) => s.setProductCards);
  const setSellerCards = useChatStore((s) => s.setSellerCards);
  const addCitations = useChatStore((s) => s.addCitations);
  const setFollowups = useChatStore((s) => s.setFollowups);
  const setStage = useChatStore((s) => s.setStage);
  const setMaterials = useChatStore((s) => s.setMaterials);
  const setPlan = useChatStore((s) => s.setPlan);
  const setClarify = useChatStore((s) => s.setClarify);
  const mergeExploredNeedIds = useChatStore((s) => s.mergeExploredNeedIds);
  const beginNeedFetch = useChatStore((s) => s.beginNeedFetch);
  const completeNeedFetch = useChatStore((s) => s.completeNeedFetch);
  const failNeedFetch = useChatStore((s) => s.failNeedFetch);
  const clearNeedFetch = useChatStore((s) => s.clearNeedFetch);
  const pushNeedFetchTurn = useChatStore((s) => s.pushNeedFetchTurn);
  const appendToken = useChatStore((s) => s.appendToken);
  const commitAssistantTurn = useChatStore((s) => s.commitAssistantTurn);
  const setStreamError = useChatStore((s) => s.setStreamError);
  const stopStreaming = useChatStore((s) => s.stopStreaming);
  const popPendingUserTurn = useChatStore((s) => s.popPendingUserTurn);
  const resetStreaming = useChatStore((s) => s.resetStreaming);
  const setClarifyDraft = useChatStore((s) => s.setClarifyDraft);
  const closeClarifyRound = useChatStore((s) => s.closeClarifyRound);
  const reopenClarifyRound = useChatStore((s) => s.reopenClarifyRound);

  const isDesktop = useIsDesktop();
  /*
   * Does this render get the imported design (≥1200px AND light theme)?
   * `AssistantFrame` asks the same question for itself; this copy serves the
   * decisions the frame cannot make from outside. (It once also gated the
   * product's own motif backdrop, removed 2026-10-15 with the textured ground.)
   */
  const design = useAssistantDesign();

  // Smart Recommendations R1: derived Set views of the Record-shaped store
  // fields (Records serialize/diff cleanly in Zustand; Sets are the shape
  // MessageList/RecommendationPlan want to render + query against).
  const exploredNeedIdSet = useMemo(
    () => new Set(Object.keys(exploredNeedIds)),
    [exploredNeedIds],
  );
  const needLoadingSet = useMemo(
    () =>
      new Set(
        Object.entries(needFetchStatus)
          .filter(([, v]) => v === "loading")
          .map(([k]) => k),
      ),
    [needFetchStatus],
  );
  const needErroredSet = useMemo(
    () =>
      new Set(
        Object.entries(needFetchStatus)
          .filter(([, v]) => v === "error")
          .map(([k]) => k),
      ),
    [needFetchStatus],
  );
  const anyNeedLoading = needLoadingSet.size > 0;

  // The "See all →" full-category sheet target (Smart Recommendations R1
  // task 4) — null = closed.
  const [seeAllNeed, setSeeAllNeed] = useState<Need | null>(null);

  // The message-list scroll container, located by its data-testid; used to
  // cache + restore per-thread scroll offset on switch (AC#5).
  const messageAreaRef = useRef<HTMLDivElement | null>(null);
  // The composer's imperative handle. SCRUM-146: the empty-state «Разобрать
  // мою смету» row drives the composer's own file picker through it (the
  // hidden <input type="file"> and the draft list both live inside
  // MessageInput); it also puts text back in the box (`prefill`) and writes
  // the guest paste path's lead-in (`prependLeadIn`).
  const composerRef = useRef<MessageInputHandle | null>(null);
  // The server's thread id for the CURRENT conversation, captured from the
  // `thread` SSE frame. A fresh chat sends thread_id=null; the server derives an
  // id and echoes it here so the NEXT turn continues the SAME thread (without
  // this, every turn starts a new thread and a follow-up can't see a document
  // attached a turn earlier → ungrounded answers). Kept in a ref so capturing it
  // mid-stream never re-renders or triggers the thread-detail query (which would
  // clobber the live-streaming turn). Reset on "New chat".
  const serverThreadIdRef = useRef<string | null>(null);
  // SCRUM bug-wave 2026-09-02 — the text of the user turn whose send is
  // currently unresolved-failed (rate limited / network / etc.), or `null`
  // once it succeeds. `handleSend` below reads this to decide whether a new
  // call is a genuine new message (push a bubble) or the user retrying the
  // SAME failed message — by retyping it, or via the error slot's own retry
  // button, which resends `lastUserText` verbatim (see MessageList.tsx). A
  // retry of the identical text must NOT push a second bubble: before this
  // fix, `pushUserTurn` ran unconditionally on every `handleSend`, so four
  // rate-limited retries left four identical orphaned user bubbles with no
  // assistant reply (SCRUM bug-wave evidence). Rolling the optimistic turn
  // back instead was considered and rejected: `lastUserText`
  // (MessageList.tsx) is explicitly designed to resole a failed send's own
  // text for a no-retype retry, so removing the bubble would have to be
  // paired with restoring the text into the composer — more moving parts for
  // the same result. Deduping the push keeps the ONE bubble that is already
  // the correct retry anchor and never lets it multiply. Cleared on success
  // (`onDone`) and on a thread switch (the store already wipes `localTurns`
  // there; this ref must not survive into the new thread).
  const pendingFailedTextRef = useRef<string | null>(null);
  /**
   * The clarifying-question answer on the wire, or null (W3). The round closes
   * locally the moment it is submitted; if the answer fails before its first
   * token the server reopens it (spec "Answer turn" step 5), so the card must
   * re-dock and the optimistic summary bubble must go — it was never the start
   * of an answer. Cleared by `done`, by any error, by Stop and by a store reset.
   */
  const clarifySubmitRef = useRef<{ roundId: string; summary: string } | null>(null);
  /**
   * What the primary stream's current (or last) turn is (W3.2 fix round). A
   * `clarify_answer` streams in the composer with Stop even at the guest
   * message wall, and if it fails after its first token its error offers no
   * resend: that would post the summary as a plain chat message, and the round
   * is already answered (a `clarify_answer` retry would 409).
   */
  const [primaryTurn, setPrimaryTurn] = useState<"chat" | "clarify_answer">("chat");
  // A pending mid-stream thread switch awaiting user confirmation (AC#14).
  const [pendingSwitchId, setPendingSwitchId] = useState<string | null>(null);
  /** Mobile chat-history sheet. Lifted out of ThreadList so the trigger can
   *  live in the chat header, below the AppShell chrome that was covering it. */
  const [historyOpen, setHistoryOpen] = useState(false);

  // Persisted messages for the open thread — skipped for a brand-new chat and
  // for the thread this tab adopted on `done` (W0.2: its local turns ARE the
  // transcript; a reload fetches it through `?t=`).
  //
  // B1 — every usage keys on `openThreadId`, not `activeThreadId` (see the
  // B1 note above `openThreadId`).
  //
  // W2.6 — the transcript is withheld, not just left unfetched: while a guest
  // conversation awaits its claim (`?t=` may still name the guest's thread),
  // and for the thread a dropped claim or an in-place reset left in the URL.
  // A disabled query still SERVES its cache entry, and the guest's own read
  // of that thread survives an SPA sign-in in this tab's QueryClient (fix
  // round: the 409 path painted it for one commit), so the data is gated
  // below as well as the fetch.
  const withholdTranscript =
    claimPending ||
    (activeThreadId === null &&
      openThreadId !== null &&
      openThreadId === staleThreadIdRef.current);
  const fetchThread =
    openThreadId !== null &&
    openThreadId !== localThreadId &&
    // A guest reads only with a guest session (W2.4c).
    (!guest || guestToken !== null) &&
    !withholdTranscript;
  const threadQuery = useQuery({
    queryKey: [...threadQueryKey(openThreadId), messageLimit],
    queryFn: async (): Promise<ThreadDetailResponse> => {
      const path = `/chat/threads/${openThreadId}?limit=${messageLimit}`;
      // Guest-allowed route (own thread only — contract).
      return guest
        ? apiClient.get<ThreadDetailResponse>(path, { allowGuest: true })
        : apiClient.get<ThreadDetailResponse>(path);
    },
    enabled: fetchThread,
    // B1 — keep the previous window on a `messageLimit` bump ("load
    // earlier"), scoped to the SAME thread. `queryKey[2]` is the thread id
    // (see `THREAD_KEY_ID_INDEX` below) — the key also changes on a thread
    // SWITCH, where showing thread A's transcript under thread B would be a
    // worse bug than the blank state this is fixing, so that case falls
    // through to `undefined` (no placeholder) same as before.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[THREAD_KEY_ID_INDEX] === openThreadId
        ? prev
        : undefined,
  });
  /** What this render may show of the thread (see `withholdTranscript`). */
  const transcript = withholdTranscript ? undefined : threadQuery.data;

  // A different thread starts from the default window again.
  useEffect(() => {
    setMessageLimit(THREAD_MESSAGE_LIMIT);
  }, [activeThreadId]);

  // The store already wipes `localTurns` on a thread switch
  // (`setActiveThreadId`) — the failed-retry text tracked in
  // `pendingFailedTextRef` must not survive past that, or a same-text send on
  // the NEW thread would wrongly skip pushing its own first bubble. (The turn
  // still on the wire is muted by the store subscription after
  // `useChatStream`, below.)
  useEffect(() => {
    pendingFailedTextRef.current = null;
  }, [activeThreadId]);

  // B3 — clamp at the server's own ceiling. Unclamped this stepped past it
  // (200 → 400 → 600) and the third click 422'd.
  const handleLoadEarlier = useCallback((): void => {
    setMessageLimit((n) =>
      Math.min(n + THREAD_MESSAGE_STEP, THREAD_MESSAGE_MAX_LIMIT),
    );
  }, []);

  // B1 — surface the thread-detail fetch's own pending/error state to
  // MessageList so it can gate the empty state instead of flashing it while
  // the real transcript is still loading (or forever, on a fetch that never
  // succeeds — `retry: 2` per `lib/queryClient.ts`, then silence).
  // A disabled query reports `isPending` forever, so both states are scoped to
  // a thread this route actually fetches (W0.2).
  // For a guest a 403 also means "this thread is not yours to read" — a retry
  // cannot help, a new chat can. A guest 401 does NOT (final web fix round,
  // item 4): it reaches here only after the guest refresh failed to reach a
  // verdict (503 `guest.unavailable` while Redis is down, a 5xx, offline —
  // B-3 keeps the session), so it is "failed" (retryable) and the pointer
  // keeps its thread. A session that really ended is the refresh's own 401
  // (B-5): that clears the token and the pointer, and the stale-`?t=` effect
  // below starts a new chat.
  const notFoundStatuses = guest ? [400, 403, 404, 422] : [400, 404, 422];
  const threadPending = fetchThread && threadQuery.isPending;
  const threadError: "notFound" | "failed" | null =
    !fetchThread || !threadQuery.isError
      ? null
      : threadQuery.error instanceof ApiError &&
          notFoundStatuses.includes(threadQuery.error.status)
        ? "notFound"
        : "failed";
  // 400/422 map to "notFound" alongside 404 deliberately: `thread_id` is a
  // server-side `uuid.UUID`, so a truncated or tampered `?t=` that isn't a
  // UUID returns 422 — a Retry can never succeed there, and "New chat" (via
  // `handleNewChat` below) is the only real exit.
  const handleRetryThread = useCallback((): void => {
    void threadQuery.refetch();
  }, [threadQuery]);

  // Guest: the server's allowance on mount and on focus — an admin raising
  // the limit (or the budget resetting at midnight) clears the sign-up card
  // without a send. The result is captured (`dataUpdatedAt`) for the
  // stop-clearing effect below, not just fired for its side effect.
  const guestMeQuery = useQuery({
    queryKey: GUEST_ME_QUERY_KEY,
    queryFn: async () => {
      const me = await fetchGuestMe();
      // Fix round 1, item 4c: skip the write if the pointer was cleared
      // WHILE this request was in flight (e.g. the `guest_claimed` handler's
      // `clearGuestPointer()`) — otherwise this background sync resurrects a
      // pointer that handling just deliberately removed.
      if (readGuestPointer() !== null) syncPointerFromMe(me);
      return me;
    },
    enabled: guest && guestToken !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // A fresh allowance that is not exhausted ends a refusal's stop. A
  // `guest.disabled` stop that is still true comes straight back on the next
  // send, so clearing it here is self-correcting.
  //
  // Fix round 1, item 4a: depends on the allowance's own PRIMITIVE fields,
  // not the `guestAllowance` object itself. `useGuestPointer`'s snapshot
  // re-parses the pointer's JSON on every write, so `guestAllowance` is a
  // NEW reference on ANY pointer write — including ones that never touch
  // `allowance` at all (e.g. "New chat" clearing only `threadId`). Depending
  // on the object meant this effect re-ran on every unrelated write and
  // wiped a `disabled` stop that has nothing to do with the allowance.
  //
  // Fix round 2, item 2 (probe P-F): the primitive comparison alone missed
  // the case where the refusal's own allowance was ALREADY "ok" before and
  // after (a `guest.disabled` stop set while the limit itself was never the
  // problem) — nothing numeric ever moves, so that version of this effect
  // never re-ran and an admin re-enabling guest mode left the guest stuck on
  // the sign-up card until a reload. `guestMeQuery.dataUpdatedAt` changes on
  // EVERY successful `/auth/guest/me`, independent of whether the payload's
  // values moved — the "a fresh read from the server just happened, and it
  // did not refuse us" signal this needs. An unrelated pointer write (e.g.
  // "New chat") still does not clear the stop: it touches neither the
  // allowance's primitives nor this query's own freshness.
  //
  // W2.6 (W2.4c carry a, probe P-I): "not exhausted" must be KNOWN, never
  // assumed. With storage blocked (private mode) the pointer never holds an
  // allowance, and the old guard read that unknown as "not exhausted" on any
  // fresh `/auth/guest/me` — even one still saying "exhausted" — so a guest
  // at the limit got the composer back for one more refused send. The
  // server's own answer decides when the pointer has none; with neither, the
  // stop stays.
  const guestAllowanceState = guestAllowance?.state ?? null;
  const guestAllowanceUsed = guestAllowance?.messages_used ?? null;
  const guestAllowanceMax = guestAllowance?.max_messages ?? null;
  const guestAllowanceReason = guestAllowance?.reason ?? null;
  const guestMeAllowanceState = guestMeQuery.data?.allowance?.state ?? null;
  useEffect(() => {
    const known = guestAllowanceState ?? guestMeAllowanceState;
    if (known === null || known === "exhausted") return;
    setGuestStop(null);
  }, [
    guestAllowanceState,
    guestAllowanceUsed,
    guestAllowanceMax,
    guestAllowanceReason,
    guestMeAllowanceState,
    guestMeQuery.dataUpdatedAt,
  ]);

  const messages: ThreadMessage[] = useMemo(
    () => transcript?.messages ?? [],
    [transcript],
  );

  /*
   * The clarifying-question round that holds the composer's slot (spec
   * "Frontend" → Placement): the committed local round turn or, after a
   * reload, the latest persisted assistant row whose round is still open —
   * never mid-stream. Precedence: an open round, then the guest sign-up card,
   * then the composer; at the message wall only a round answered through
   * `clarify_answer` (free there) may take the slot (roundMayDock, N9).
   */
  const openRound = useMemo(
    () =>
      selectOpenRound({
        messages,
        localTurns,
        closedRoundIds: closedClarifyRounds,
        isStreaming,
      }),
    [messages, localTurns, closedClarifyRounds, isStreaming],
  );
  const dockedRound =
    openRound !== null && roundMayDock(openRound.kind, guestStopCause) ? openRound : null;
  // Every docked round — a lead-in round, a follow-up, a smart-rec round, a
  // re-dock after reload — anchors the transcript at the end of its turn's
  // text, above the card (controller rulings, 2026-10-13; MessageList +
  // answer-anchor.ts).
  const answerAnchorRoundId = dockedRound?.round_id ?? null;
  /*
   * W3.2 fix round — the slot while an answer streams: the composer, with
   * Stop, even for a guest at the message wall (the answer is free there);
   * the sign-up card takes the slot back on `done`. Precedence: an open round,
   * then a streaming answer, then the sign-up card, then the composer.
   */
  const answerStreaming = isStreaming && primaryTurn === "clarify_answer";
  const answerHoldsWall = guestStopCause !== null && answerStreaming;
  const answerHeldWallRef = useRef(answerHoldsWall);
  useEffect(() => {
    answerHeldWallRef.current = answerHoldsWall;
  }, [answerHoldsWall]);
  // The render where the sign-up card takes the slot back from the answer's
  // composer: focus there drops to <body> when the composer unmounts, so the
  // card takes it — unless the user had already moved it elsewhere.
  const gateCardFocusAfterAnswer =
    guestStopCause !== null &&
    !answerHoldsWall &&
    dockedRound === null &&
    answerHeldWallRef.current &&
    focusLeavesWithComposer(messageAreaRef.current);

  // I4: the held sign-up sheet opens once no round holds the slot and no turn
  // streams — the answer's `done`, ✕, Stop, a thread change. An answer that
  // fails before its first token re-docks its round in the same render, so
  // the sheet stays held for the retry. The sheet opens after the sign-up
  // card has taken focus, so closing it hands focus back to the card.
  useEffect(() => {
    if (heldGate === null || isStreaming || dockedRound !== null) return;
    setGate(heldGate);
    setHeldGate(null);
  }, [heldGate, isStreaming, dockedRound]);

  /*
   * C1 (owner, 2026-10-08; ui-review.md §4.4). On the design frame an empty
   * conversation used to leave a 363–543px blank band between the greeting
   * at the top and the composer docked at the bottom. While the conversation
   * is empty, the greeting and the composer are now one group, centred in the
   * pane BELOW the «Помощник» bar — which the owner kept, so the pane is
   * centred by two equal spacers rather than by `justify-center` on <main>,
   * which would pull the bar down into the middle as well. `MessageList`
   * stops filling the pane on the same answer (it takes this as its
   * `centred` prop, never re-deriving it). The first message removes both
   * spacers, and the composer docks at the bottom as before. Below 1200px,
   * and in dark theme, nothing changes.
   *
   * Only a real composer is centred (fix round 1, I3). A guest who has
   * spent the allowance and starts «Новый чат» has an empty conversation,
   * but the composer is replaced by the sign-up card: footer chrome (a
   * full-width ruled band with a safe-area inset) that floated mid-pane
   * under C1. While a stop cause holds, the layout stays docked.
   */
  const centreEmpty =
    design &&
    !claimPending &&
    guestStopCause === null &&
    isConversationEmpty({
      messageCount: messages.length,
      localTurnCount: localTurns.length,
      isStreaming,
      streamError,
      threadPending,
      threadError,
    });

  // Smart Recommendations R1 rehydration: every plan Need ever surfaced in
  // this thread (union of every message's `plan_json`), used ONLY to derive
  // which of them are already "explored" on a reload — see
  // `plan-rehydrate.ts` for the (flagged, best-effort) derivation heuristic.
  const rehydratedPlanNeeds = useMemo(
    () => collectPlanNeeds(messages),
    [messages],
  );
  useEffect(() => {
    if (rehydratedPlanNeeds.length === 0) return;
    const derived = deriveExploredNeedIds(messages, rehydratedPlanNeeds);
    if (derived.size > 0) mergeExploredNeedIds(derived);
    // Re-derive whenever the active thread's messages change (initial load,
    // thread switch, or a post-fetch invalidation refetch).
  }, [messages, rehydratedPlanNeeds, mergeExploredNeedIds]);

  /*
   * Map a terminal stream error to a localized toast (AC #5/#13; AC#6 is
   * DELIBERATELY not followed to the letter here — see the rate-limit note
   * below).
   *
   * Two things are deliberate for the codes that DO toast. First,
   * `id: "chat-stream-error"` — the error slot in the transcript ALREADY
   * states the failure, so the toast is the peripheral notice, and a user
   * who retries twice should see one notice update rather than a stack of
   * three saying the same thing.
   *
   * Second, only `network_error` is warned rather than errored: a dropped
   * connection is the one code here the user can act on immediately and is
   * usually transient.
   *
   * GUARDRAILS rule 10 call-out (bug-wave 2026-09-02): STORY-002-009 AC#6
   * literally requires a toast for `rate_limited`. QA evidence (a tester saw
   * FOUR orphaned bubbles, the in-transcript error slot, AND a toast — all
   * repeating the identical "not so fast" sentence for the ONE event) showed
   * that toast to be duplicated messaging, not a second useful surface, so
   * this intentionally deviates from the AC's letter while keeping its
   * intent: the `StreamErrorSlot` in the transcript (MessageList.tsx) already
   * states the truthful per-bucket wait with the retry-after seconds, sits
   * where the user is already looking, and — unlike a toast — does not
   * auto-dismiss while the wait is still running, so it is the strictly
   * better single surface for a "wait N seconds" condition. `not_implemented`
   * (AC#5) and `network_error` (AC#13) are untouched — those ACs are still
   * followed to the letter, and `budget_exhausted`'s pre-existing
   * toast+slot duplication is unrelated to this evidenced bug and left alone
   * (out of this wave's scope).
   */
  const toastForError = useCallback(
    (error: StreamErrorEvent): void => {
      const id = "chat-stream-error";
      switch (error.code) {
        case "not_implemented_in_mvp_sprint":
          notify.info(t("errors.notImplementedToast"), { id });
          break;
        case "rate_limited_user":
        case "rate_limited_ip":
          // No toast — see the call-out above. The transcript's
          // `StreamErrorSlot` is the single surface for this event.
          break;
        case "budget_exhausted":
          notify.warning(t("errors.budgetExhausted"), { id });
          break;
        case "network_error":
          notify.error(t("errors.connectionLostToast"), { id });
          break;
        case "session_expired":
          notify.error(t("errors.sessionExpired"), { id });
          break;
        default:
          notify.error(t("errors.generic"), { id });
          break;
      }
    },
    [t],
  );

  // Keep the latest toast handler without re-arming the stream callbacks.
  const toastForErrorRef = useRef(toastForError);
  toastForErrorRef.current = toastForError;

  // Set after `handleNewChat` exists (below); called from the stream's onError.
  const guestRefusalRef = useRef<(e: StreamErrorEvent) => void>(() => undefined);
  // Set after `handleNewChat` and `guestRefusalRef` exist (below); called from
  // the stream's onError for a `clarify_answer` that failed before its first token.
  const clarifySubmitErrorRef = useRef<
    (e: StreamErrorEvent, submit: { roundId: string; summary: string }) => void
  >(() => undefined);
  // Set after `setRoundStatus` exists (below); called from the stream's
  // onError for a `clarify_answer` that failed AFTER its first token.
  const clarifyAnswerBrokeRef = useRef<
    (submit: { roundId: string; summary: string }) => void
  >(() => undefined);

  // `abortSend` cancels the transport itself (the store's `stopStreaming`
  // only resets the UI state): an abandoned send dispatches nothing more
  // (W2.6 — the dead-thread recovery and an in-place session reset).
  // `detachSend` mutes it the same way but lets the request run, so the
  // server finishes and saves the turn (a thread change — final fix wave, I1).
  const { send, abort: abortSend, detach: detachSend } = useChatStream({
    // Any frame at all ends the «Соединяемся…» wait for this turn. The hook
    // only dispatches a LIVE send's frames, so a muted turn's late ones
    // never stop the next turn's clock.
    onFrame: () => markStreamFrame(),
    onThreadId: (id) => {
      // Continue THIS thread on the next turn (enables document grounding).
      serverThreadIdRef.current = id;
    },
    onIntent: (e) => setRoutingIntent(e.intent),
    onToken: (value) => appendToken(value),
    onProducts: (items) => setProductCards(items),
    onSellerCards: (items) => setSellerCards(items),
    // Accumulate KB sources from BOTH the up-front `retrieval` kb_used[] and the
    // per-source `citation` frames (de-duped by slug in the store).
    onRetrieval: (sources) => addCitations(sources),
    onCitation: (e) => addCitations([e]),
    onFollowups: (items) => setFollowups(items),
    // Smart Recommendations R1: `plan` and `followups` are mutually
    // exclusive server-side (one suggestion surface per turn) — no client
    // dedup needed.
    onPlan: (items) => setPlan(items),
    onClarify: (e) => setClarify(e),
    // A TYPED turn the orchestrator resolved against the plan (spec §3.2b
    // "fetch" outcome) rides THIS primary stream, not the dedicated
    // fetch_need stream below — route its cards through the SAME
    // `currentProductCards` slot a normal product turn uses (so it commits +
    // renders identically) and mark the Need explored immediately.
    onNeedProducts: (needId, items) => {
      setProductCards(items);
      completeNeedFetch(needId);
    },
    // SCRUM-281: the step running right now. `attachment_status` predates the
    // stage frame and means the same thing — the server follows it with
    // `stage: reading_document` carrying the file count.
    onStage: (e) => setStage(e),
    onAttachmentStatus: () => setStage({ key: "reading_document" }),
    onMaterials: (m) => setMaterials(m),
    onAllowance: (allowance) => {
      // Fix round 1, item 4f: members unchanged BY CONSTRUCTION, not merely
      // because the server contract says member streams never carry this
      // frame — the client must not depend on that discipline alone.
      if (!guest) return;
      if (guestPointerWritable()) updateGuestPointer({ allowance, hasSession: true });
      pendingAllowanceRef.current = allowance;
    },
    onError: (e) => {
      const submit = clarifySubmitRef.current;
      clarifySubmitRef.current = null;
      if (submit !== null && useChatStore.getState().currentAssistantText.length === 0) {
        clarifySubmitErrorRef.current(e, submit);
        return;
      }
      // After its first token an answer that fails was not saved either, and
      // the server reopened the round: the card comes back as the retry, and
      // the error slot below keeps no resend (re-review ruling).
      if (submit !== null && e.code !== "clarify_round_closed") {
        clarifyAnswerBrokeRef.current(submit);
      }
      if (guest && isGuestStreamErrorCode(e.code)) {
        guestRefusalRef.current(e);
        return;
      }
      const mapped = {
        code: e.code as never,
        ...(e.intent !== undefined ? { intent: e.intent } : {}),
        ...(e.retryAfterSeconds !== undefined
          ? { retryAfterSeconds: e.retryAfterSeconds }
          : {}),
      };
      setStreamError(mapped);
      toastForErrorRef.current(e);
    },
    onDone: (payload) => {
      // This send resolved — it is no longer "the failed text a retry would
      // dedupe against" (see `pendingFailedTextRef`).
      pendingFailedTextRef.current = null;
      clarifySubmitRef.current = null;
      // Commit the assistant turn to local session state (AC #4). The `done`
      // event carries the persisted assistant message_id (Phase 5) — thread it
      // onto the committed turn so its thumbs-feedback control has a target.
      // An existing thread is invalidated so the durable copy reloads; a
      // brand-new chat adopts its server thread instead (W0.2).
      const messageId =
        typeof payload["message_id"] === "string"
          ? (payload["message_id"] as string)
          : undefined;
      // The user's OWN persisted row id. Without it the optimistic question
      // bubble can never be matched against the transcript the invalidation
      // below refetches, so it re-renders under the persisted answer — the
      // "answer above the question" report (SCRUM-238). Null/absent means the
      // server did not persist the turn; the local bubble then stays, because
      // it is the only copy that exists.
      const userMessageId =
        typeof payload["user_message_id"] === "string"
          ? (payload["user_message_id"] as string)
          : undefined;
      // W0.2 — a brand-new chat learned its server thread id from the
      // `thread` frame; the turn is persisted now, so adopt it. The
      // store→URL mirror effect writes `?t=` (replace), and a reload restores
      // the conversation. Adopting on the `thread` frame instead would reset
      // the live stream and fetch a thread the server has not saved yet.
      const adoptedThreadId =
        activeThreadId === null ? serverThreadIdRef.current : null;
      // The round this turn asked, read before the commit clears it (I4).
      const turnRoundKind = useChatStore.getState().currentClarify?.kind ?? null;
      commitAssistantTurn(messageId, userMessageId);
      if (adoptedThreadId !== null) {
        adoptServerThread(adoptedThreadId);
        // Guest: the pointer remembers the conversation for a reload and for
        // the sign-in return path (`/chat?t=<id>`).
        if (guest && guestPointerWritable()) {
          updateGuestPointer({ threadId: adoptedThreadId, hasSession: true });
        }
      } else if (activeThreadId !== null) {
        void queryClient.invalidateQueries({
          queryKey: threadQueryKey(activeThreadId),
        });
      }
      // Guest: the finished turn's allowance decides whether the sign-up
      // sheet opens now — at the limit (hard), or every K messages past it
      // (soft). The answer has already finished; nothing interrupts it.
      const turnAllowance = pendingAllowanceRef.current;
      pendingAllowanceRef.current = null;
      //
      // Final fix wave, I4: a clarify or quote round this turn docks holds
      // the sheet back (`heldGate`) — the round is free to answer at the wall
      // (spec "Frontend" → Placement), and the sheet would cover it. A
      // smart-rec round does not dock at the wall (N9), so its sheet opens now.
      if (guest && turnAllowance !== null && turnAllowance.show_prompt) {
        const exhausted = turnAllowance.state === "exhausted";
        const turnGate: GuestGate = exhausted
          ? { reason: "limit", cause: stopCauseForAllowance(turnAllowance), hard: true }
          : { reason: "limit", cause: "messages", hard: false };
        const roundHoldsSlot =
          (turnRoundKind === "clarify" || turnRoundKind === "quote") &&
          roundMayDock(turnRoundKind, exhausted ? stopCauseForAllowance(turnAllowance) : null);
        if (roundHoldsSlot) {
          setHeldGate(turnGate);
        } else {
          setGate(turnGate);
        }
      }
      // The turn may have created a new thread and triggers async AI-title
      // generation (worker, a few seconds later). Refetch the sidebar so the new
      // thread appears now; the list's refetchInterval then polls until the
      // generated title lands — no manual reload (prefix key matches all groups).
      void queryClient.invalidateQueries({
        queryKey: threadListQueryKey(),
      });
    },
  });

  // Final fix wave, I1: the primary stream's turn belongs to the thread it was
  // sent in. On ANY change of the open thread («Новый чат», a sidebar switch,
  // back/forward) it is muted — never aborted, so the server still finishes
  // and saves it (a `clarify_answer` included; aborting lost it, the W3.2
  // deferral) — and none of its late frames (`clarify`, an error, `done`)
  // reaches the conversation now on screen. Synchronous with the store
  // change rather than an effect: a send started before a passive effect ran
  // would be the one muted. A store reset is the subscription below, which
  // aborts instead. Adoption on `done` changes the id too; muting a send
  // that has already finished is a no-op.
  //
  // The thread left with a turn still on the wire is marked stale (I2): the
  // server is saving that turn, so coming back re-reads the thread instead of
  // serving the cached transcript without it for the rest of its staleTime.
  //
  // «Новый чат» calls this itself too (re-review): during the first turn of
  // an unsaved chat the id goes null → null, so no change fires here.
  const abandonPrimaryTurn = useCallback(
    (left: string | null): void => {
      if (detachSend() && left !== null) {
        void queryClient.invalidateQueries({
          queryKey: threadQueryKey(left),
          refetchType: "none",
        });
      }
      clarifySubmitRef.current = null;
    },
    [detachSend, queryClient],
  );
  useEffect(
    () =>
      useChatStore.subscribe((state, previous) => {
        if (state.resetEpoch !== previous.resetEpoch) return;
        if (state.activeThreadId === previous.activeThreadId) return;
        abandonPrimaryTurn(previous.activeThreadId);
      }),
    [abandonPrimaryTurn],
  );

  // --- Smart Recommendations R1: the dedicated `fetch_need` stream --------
  // A chip tap / clarify pick / "show all" step is a SEPARATE turn from
  // whatever the PRIMARY stream above is doing — it must NOT touch
  // `currentAssistantText`/`isStreaming` (that would hijack the composer's
  // Stop button and the main "typing…" indicator for what is really a small
  // side action). It runs on its OWN `useChatStream` instance; taps are
  // serialized (`anyNeedLoading` guards every entry point below) so these
  // refs always describe exactly ONE in-flight fetch at a time.
  const fetchingNeedIdRef = useRef<string | null>(null);
  const fetchRationaleRef = useRef("");
  const fetchCardsRef = useRef<ProductCardData[]>([]);

  const { send: sendNeedFetch, abort: abortNeedFetch } = useChatStream({
    onToken: (value) => {
      fetchRationaleRef.current += value;
    },
    onNeedProducts: (_needId, items) => {
      fetchCardsRef.current = items;
    },
    onError: (e) => {
      // `e.needId` (from a `smart_rec.need_not_found` error) is the
      // authoritative target when present; a transport-level failure
      // (network/429/401) carries no `need_id`, so fall back to the ref.
      const needId = e.needId ?? fetchingNeedIdRef.current;
      fetchingNeedIdRef.current = null;
      fetchRationaleRef.current = "";
      fetchCardsRef.current = [];
      // SCRUM-274: the server stopped the tap cold (the message limit, the
      // token cap, the daily budget, guest mode off) before the allowance in
      // memory caught up. Handled like a refused send: the composer becomes
      // the card and the sign-up sheet opens. Not a failed fetch, so the chip
      // goes back to idle with no error.
      const cause =
        guest && isGuestStreamErrorCode(e.code)
          ? stopCauseFor(e.code, e.guestReason ?? null)
          : null;
      if (cause !== null) {
        if (needId) clearNeedFetch(needId);
        if (e.allowance !== undefined && guestPointerWritable()) {
          updateGuestPointer({ allowance: e.allowance, hasSession: true });
        }
        guestStopCauseRef.current = cause;
        setGuestStop(cause);
        setGate({ reason: "limit", cause, hard: true });
        return;
      }
      if (needId) failNeedFetch(needId);
    },
    onDone: (payload) => {
      const needId = fetchingNeedIdRef.current;
      if (needId) {
        // The persisted assistant row for THIS chip tap. Without it the local
        // copy below outlives the refetch and the chip's reply renders twice
        // ("I couldn't find any matches for ..." appearing stacked) — SCRUM-188.
        const needMessageId =
          typeof payload["message_id"] === "string"
            ? (payload["message_id"] as string)
            : undefined;
        pushNeedFetchTurn(
          fetchRationaleRef.current,
          fetchCardsRef.current,
          needMessageId,
        );
        completeNeedFetch(needId);
        if (activeThreadId !== null) {
          void queryClient.invalidateQueries({
            queryKey: threadQueryKey(activeThreadId),
          });
        }
      }
      fetchingNeedIdRef.current = null;
      fetchRationaleRef.current = "";
      fetchCardsRef.current = [];
    },
  });

  // One Need fetch — shared by a plan-chip tap, a smart-rec round pick, and
  // each step of the «Просто покажите всё» loop.
  const doFetchNeed = useCallback(
    async (
      needId: string,
      facet?: string,
      // R2 Phase B. Kept as a separate bag from `facet` (R1's free-text
      // semantic hint) because they are different mechanisms that happen to
      // share a word: `facet` nudges the vector query, these apply a
      // structured attribute filter.
      r2?: { facetField?: string; facetValue?: string; facetSkip?: boolean },
      // The smart-rec round this fetch answers (contract C7 `round_id`), so
      // the server marks it and it can never loop; `clarifySkip` marks the
      // FIRST fetch of an R1 skip (N8).
      round?: { roundId: string; clarifySkip?: boolean },
    ): Promise<void> => {
      // SCRUM-274 (owner, 2026-10-09): a guest at the limit — the same
      // condition that swaps the composer for the sign-up card — gets the
      // sign-up sheet from a chip or a "show me everything" step, and nothing
      // is fetched. Before the limit the chips stay free. «Смотреть все →» is
      // not a `fetch_need` and stays the public listing.
      const stop = guestStopCauseRef.current;
      if (stop !== null) {
        setGate({ reason: "limit", cause: stop, hard: true });
        return;
      }
      const threadId = activeThreadId ?? serverThreadIdRef.current;
      if (threadId === null) return; // schema requires thread_id — no-op
      beginNeedFetch(needId);
      fetchingNeedIdRef.current = needId;
      fetchRationaleRef.current = "";
      fetchCardsRef.current = [];
      await sendNeedFetch("", {
        threadId,
        language: resolveLanguage(i18n.language),
        action: "fetch_need",
        needId,
        ...(guest ? { guest: true } : {}),
        facet,
        ...(r2?.facetField !== undefined ? { facetField: r2.facetField } : {}),
        ...(r2?.facetValue !== undefined ? { facetValue: r2.facetValue } : {}),
        ...(r2?.facetSkip ? { facetSkip: true } : {}),
        ...(round !== undefined ? { roundId: round.roundId } : {}),
        ...(round?.clarifySkip ? { clarifySkip: true } : {}),
      });
    },
    [activeThreadId, beginNeedFetch, guest, i18n.language, sendNeedFetch],
  );

  // A plan-chip tap (RecommendationPlan) replays its Need as a `fetch_need`.
  const handleFetchNeed = useCallback(
    (needId: string): void => {
      if (anyNeedLoading) return;
      void doFetchNeed(needId);
    },
    [anyNeedLoading, doFetchNeed],
  );

  // "See all →" (RecommendationPlan) — opens the full, filterable category
  // sheet for one Need (Smart Recommendations R1 task 4).
  const handleSeeAllNeed = useCallback((need: Need): void => {
    setSeeAllNeed(need);
  }, []);

  // --- Clarifying-question rounds (W3) -------------------------------------
  // Answering goes through the PRIMARY stream as a `clarify_answer` turn
  // (contract C7): the summary bubble appears at once (the server persists the
  // same text, C12, and its row replaces this one on refetch), the answer
  // streams with Stop, and the composer comes back on `done`.
  //
  // Every round closes (and reopens) through `setRoundStatus`: the store's
  // thread-scoped record AND the status the server now holds, written into
  // every cached window of the thread (final fix wave, I2). A thread switch
  // wipes the store's record, and a switch back within the query's staleTime
  // would otherwise re-dock the round from the cached `open` copy.
  const setRoundStatus = useCallback(
    (roundId: string, status: ClarifyStatus): void => {
      if (status === "open") reopenClarifyRound(roundId);
      else closeClarifyRound(roundId);
      const threadId = activeThreadId ?? serverThreadIdRef.current;
      if (threadId === null) return;
      queryClient.setQueriesData<ThreadDetailResponse>(
        { queryKey: threadQueryKey(threadId) },
        (data) => withRoundStatus(data, roundId, status),
      );
    },
    [activeThreadId, closeClarifyRound, queryClient, reopenClarifyRound],
  );

  const handleClarifySubmit = useCallback(
    (round: ClarifyRoundEvent, answers: ClarifyAnswerInput[], skip: boolean): void => {
      setRoundStatus(round.round_id, skip ? "skipped" : "answered");
      const threadId = activeThreadId ?? serverThreadIdRef.current;
      // A round always arrives on a thread (C8). One with nothing to answer
      // into closes rather than leaving «Готово» dead (final fix wave, I1).
      if (threadId === null) return;
      const language = resolveLanguage(i18n.language);
      const summary = buildClarifySummary(round, answers, { skip, language });
      pushUserTurn(summary, undefined, { answersRound: round.round_id });
      pendingFailedTextRef.current = null;
      clarifySubmitRef.current = { roundId: round.round_id, summary };
      setPrimaryTurn("clarify_answer");
      beginStream(new AbortController());
      void send("", {
        threadId,
        language,
        action: "clarify_answer",
        roundId: round.round_id,
        clarifyAnswers: skip ? [] : answers,
        ...(skip ? { clarifySkip: true } : {}),
        ...(guest ? { guest: true } : {}),
      });
    },
    [activeThreadId, beginStream, guest, i18n.language, pushUserTurn, send, setRoundStatus],
  );

  // ✕ / Escape (spec "Outcomes"): the card closes now, whatever the network
  // does. One silent retry lives in `dismissClarifyRound`; a round the server
  // had already closed (404/409) re-reads the thread; a dismiss that never
  // landed leaves the round open server-side, and it re-docks on the next
  // reload (spec "Failure handling").
  const handleClarifyDismiss = useCallback(
    (round: ClarifyRoundEvent): void => {
      setRoundStatus(round.round_id, "dismissed");
      const threadId = activeThreadId ?? serverThreadIdRef.current;
      if (threadId === null) return;
      void dismissClarifyRound({ threadId, roundId: round.round_id, guest }).then((outcome) => {
        if (outcome === "closed") {
          void queryClient.invalidateQueries({ queryKey: threadQueryKey(threadId) });
        }
      });
    },
    [activeThreadId, guest, queryClient, setRoundStatus],
  );

  // A smart-rec round's option (spec "Smart-rec questions"): the same
  // `fetch_need` side stream as before — no bubble — now carrying the round_id,
  // so the server marks the round answered (T6) and it can never loop. The
  // card closes at once.
  const handleClarifyPick = useCallback(
    (round: ClarifyRoundEvent, option: ClarifyRoundOption): void => {
      const filter = option.filter;
      if (anyNeedLoading || filter === undefined || filter === null) return;
      setRoundStatus(round.round_id, "answered");
      const { need_id: needId, facet_field: facetField, facet_value: facetValue } = filter;
      void doFetchNeed(
        needId,
        undefined,
        facetField !== undefined && facetValue !== undefined ? { facetField, facetValue } : undefined,
        { roundId: round.round_id },
      );
    },
    [anyNeedLoading, doFetchNeed, setRoundStatus],
  );

  // The round-level escape. An ordinary or quote round is answered as skipped
  // (C7 `clarify_skip`). A smart-rec escape keeps its «Просто покажите всё»
  // meaning, now marked with the round_id (N8).
  const handleClarifySkip = useCallback(
    (round: ClarifyRoundEvent): void => {
      if (round.kind === "clarify" || round.kind === "quote") {
        handleClarifySubmit(round, [], true);
        return;
      }
      if (anyNeedLoading) return;
      const filters = (round.questions[0]?.options ?? []).flatMap((option) =>
        option.filter ? [option.filter] : [],
      );
      if (round.kind === "smart_rec_facet") {
        // R2: replay the one need with `facet_skip`. Without it the server
        // cannot tell a skip from no answer, and asks again.
        const needId = filters[0]?.need_id;
        if (needId === undefined) return;
        setRoundStatus(round.round_id, "skipped");
        void doFetchNeed(needId, undefined, { facetSkip: true }, { roundId: round.round_id });
        return;
      }
      // R1: fetch every need not explored yet, one at a time — `fetch_need`
      // reads the thread's shown cards before writing, so overlapping
      // requests could repeat cards across needs. The FIRST step carries the
      // skip marker; the rest are plain fetches.
      const pending = [...new Set(filters.map((filter) => filter.need_id))].filter(
        (id) => !exploredNeedIds[id],
      );
      const [first, ...rest] = pending;
      if (first === undefined) {
        // Nothing left to fetch, so no request would carry the skip: close the
        // round server-side the only other way there is.
        handleClarifyDismiss(round);
        return;
      }
      setRoundStatus(round.round_id, "skipped");
      void (async () => {
        await doFetchNeed(first, undefined, undefined, { roundId: round.round_id, clarifySkip: true });
        for (const needId of rest) {
          await doFetchNeed(needId);
        }
      })();
    },
    [
      anyNeedLoading,
      doFetchNeed,
      exploredNeedIds,
      handleClarifyDismiss,
      handleClarifySubmit,
      setRoundStatus,
    ],
  );

  // Stop also forgets an answer in flight: nothing it reports later may
  // re-dock the round the user just walked away from.
  const handleStop = useCallback((): void => {
    clarifySubmitRef.current = null;
    stopStreaming();
  }, [stopStreaming]);

  // Abort any live stream when the route unmounts (handled in the hook too).
  useEffect(() => {
    return () => {
      stopStreaming();
    };
    // Run only on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // W2.6 (ruling P7a) — a session change normally REMOUNTS this route: `/`
  // (HomeRoute, a guest) and `/chat` (ChatEntryRoute, a member) are different
  // route elements, so a guest signing in (HomeRoute → Navigate /chat) or a
  // member signing out (ChatEntryRoute → `/` or the login door) makes React
  // mount a NEW ChatRoute, whose refs and local state all start fresh.
  //
  // A store reset can still run while THIS instance stays mounted: another
  // tab ended this guest's session (W2.2's storage handler — the tab stays a
  // guest at `/`), the 409 claim outcome (the member stays on /chat), a
  // persistent 401 before its page redirect lands, or any bare-route
  // composition. The store cannot reach this route's per-conversation state —
  // the server thread of a fresh chat, the failed-retry text, a pending
  // mid-stream switch, the message window — and keeping it would send the
  // next turn into the previous identity's thread
  // (`activeThreadId ?? serverThreadIdRef.current`) or let the abandoned
  // stream's late `done` adopt that thread under the fresh store. So every
  // `resetAll()` drops it too, synchronously — before any late stream
  // callback or the next click — and abandons both streams' transports. The
  // URL is left alone: each in-place path owns it (the 409 navigates to
  // /chat, a guest's stale `?t=` is dropped by the effect below, and a 401's
  // redirect keeps it as the return_to); until it moves, the thread it names
  // is not fetched (`staleThreadIdRef`).
  useEffect(
    () =>
      useChatStore.subscribe((state, previous) => {
        if (state.resetEpoch === previous.resetEpoch) return;
        abortSend();
        abortNeedFetch();
        staleThreadIdRef.current = urlThreadIdRef.current;
        serverThreadIdRef.current = null;
        pendingFailedTextRef.current = null;
        clarifySubmitRef.current = null;
        pendingAllowanceRef.current = null;
        fetchingNeedIdRef.current = null;
        fetchRationaleRef.current = "";
        fetchCardsRef.current = [];
        setPendingSwitchId(null);
        setMessageLimit(THREAD_MESSAGE_LIMIT);
        setSeeAllNeed(null);
        setGate(null);
        setHeldGate(null);
        setGuestStop(null);
        setPrimaryTurn("chat");
      }),
    [abortSend, abortNeedFetch],
  );

  const handleSend = useCallback(
    (
      message: string,
      attachmentUploadIds: string[] = [],
      attachments: MessageAttachment[] = [],
      promptOrigin?: string,
    ): void => {
      // Recent prior USER messages (oldest→newest) — lets the backend resolve a
      // product noun mentioned earlier when this turn is a bare "find me options".
      const history = [...messages, ...localTurns]
        .filter((turn) => turn.role === "user")
        .map((turn) => turn.content)
        .slice(-8);
      // Render the user's bubble immediately (with any attachment chips) —
      // UNLESS this call is a retry of the exact text whose bubble is already
      // sitting there from the previous, still-unresolved-failed attempt (see
      // `pendingFailedTextRef` above). Then arm + drive the stream and thread
      // the upload ids into the request body.
      if (pendingFailedTextRef.current !== message) {
        pushUserTurn(message, attachments);
      }
      // Mark THIS text as the one currently in flight. If the send fails,
      // `onError` leaves this in place (no separate write needed there) so a
      // same-text retry is recognised above; `onDone` clears it on success.
      pendingFailedTextRef.current = message;
      // A send mid-answer (Regenerate, a follow-up) is its own turn: its
      // failure must never be taken for the answer's (final fix wave).
      clarifySubmitRef.current = null;
      setPrimaryTurn("chat");
      const controller = new AbortController();
      beginStream(controller);
      void send(message, {
        // Prefer an explicitly loaded thread (sidebar switch); otherwise the
        // server-derived id captured from the `thread` frame on a prior turn of
        // THIS fresh chat — so follow-ups continue the same thread and stay
        // grounded in any earlier attachment.
        threadId: activeThreadId ?? serverThreadIdRef.current,
        language: resolveLanguage(i18n.language),
        history,
        attachmentUploadIds,
        promptOrigin,
        ...(guest ? { guest: true } : {}),
      });
    },
    [
      activeThreadId,
      beginStream,
      guest,
      i18n.language,
      localTurns,
      messages,
      pushUserTurn,
      send,
    ],
  );

  // Starters and the "You can also ask" followup chips send as a normal user
  // message. The quote card is the one exception — see handleOpenQuoteUpload.
  const handlePickStarter = useCallback(
    (text: string, origin?: string): void => {
      handleSend(text, [], [], origin);
    },
    [handleSend],
  );

  // SCRUM-146: "Parse my quote" opens the composer's file picker instead of
  // sending. Previously it posted its own label as a chat message, which the
  // backend could only answer with "I don't see your quote yet — upload a photo
  // or PDF" — advice the greeting directly above it and the composer's
  // paperclip already give. Called synchronously from the click so the picker
  // stays inside the browser's user-activation window. A guest without
  // attachments pastes the estimate instead — the quote review reads pasted
  // estimates (spec "Locked, never a dead end"). The lead-in goes in front of
  // anything already typed, never over it (final web fix round, item 7).
  const handleOpenQuoteUpload = useCallback((): void => {
    if (attachmentsLocked) {
      composerRef.current?.prependLeadIn(t("guest.pastePrefix"));
      return;
    }
    composerRef.current?.openFilePicker();
  }, [attachmentsLocked, t]);

  const openAttachmentsGate = useCallback((): void => {
    setGate({ reason: "attachments" });
  }, []);

  const openHistoryGate = useCallback((): void => {
    setHistoryOpen(false);
    setGate({ reason: "history" });
  }, []);

  // B-7: the server refused a clip because guest voice was switched off
  // after the config was read (useTranscribe hides the mic itself).
  const openVoiceGate = useCallback((): void => {
    setGate({ reason: "voice" });
  }, []);

  const handleNewChat = useCallback((): void => {
    // The turn on the wire stays with the chat it was sent in — muted here,
    // not left to the thread-change subscription: an unsaved chat's first
    // turn has no thread id yet, and its late `clarify`/`done` would land in
    // the new chat (re-review of the final fix wave).
    abandonPrimaryTurn(useChatStore.getState().activeThreadId);
    stopStreaming();
    serverThreadIdRef.current = null; // start a brand-new server thread
    setActiveThreadId(null);
    // SCRUM-179: starting a new chat is the ONE deliberate "no thread open"
    // transition, so it owns dropping ?t= (the mirror effect never deletes).
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(THREAD_PARAM);
        return next;
      },
      { replace: true },
    );
    // Guest: forget the old thread, so a later visit to `/` does not reopen it.
    if (guest) {
      const current = readGuestPointer();
      if (current !== null && current.threadId !== null) {
        writeGuestPointer({ ...current, threadId: null });
      }
    }
  }, [abandonPrimaryTurn, guest, setActiveThreadId, setSearchParams, stopStreaming]);

  // «Не сохранять» (W2.6): the guest conversation is discarded, then a clean
  // member chat — `?t=` may still name the guest's thread.
  const handleDontSaveGuestClaim = useCallback((): void => {
    staleThreadIdRef.current = openThreadId;
    dropGuestClaim();
    handleNewChat();
  }, [openThreadId, dropGuestClaim, handleNewChat]);

  guestRefusalRef.current = (e: StreamErrorEvent): void => {
    if (!isGuestStreamErrorCode(e.code)) return;
    const code = e.code;
    const text = pendingFailedTextRef.current ?? "";
    if (e.allowance !== undefined && guestPointerWritable()) {
      updateGuestPointer({ allowance: e.allowance, hasSession: true });
    }
    const cause = stopCauseFor(code, e.guestReason ?? null);
    if (cause !== null) {
      // Refused before any LLM work: the message was never accepted. Drop
      // its bubble, keep the text for after sign-in, stop the composer.
      popPendingUserTurn(text);
      pendingFailedTextRef.current = null;
      resetStreaming();
      if (text !== "") writeGuestDraft(text);
      setGuestStop(cause);
      setGate({ reason: "limit", cause, hard: true });
      return;
    }
    if (code === "guest_session_expired") {
      // The guest session ended under us — the api-client has already
      // cleared the guest token/pointer and noted the expiry
      // (`noteGuestSessionExpired`); this handler does NOT discard anything
      // itself (W2.4a re-review). A fresh chat, the text back in the box.
      popPendingUserTurn(text);
      pendingFailedTextRef.current = null;
      handleNewChat();
      if (text !== "") composerRef.current?.prefill(text);
      // Ruling P12, fix round 1 item 4b: toast UNCONDITIONALLY — a
      // storage-blocked browser (private mode) never sets the shared
      // one-shot flag at all (`endGuestSessionLocally` only calls
      // `noteGuestSessionExpired` when it can read `hasSession` off the
      // pointer), so gating this call on `takeGuestSessionExpired()` left
      // BOTH this branch and the mount/guestToken-change effect below
      // silent in that case — no notice at all. `notify.info` shares the
      // SAME `id` with that effect's own call, and Sonner updates a toast
      // with a matching id in place rather than stacking a second one, so
      // this still shows exactly one VISIBLE toast when both happen to fire
      // for the same event. Draining the flag anyway (its return value is
      // unused here) stops it sitting `true` and firing on some later,
      // unrelated `guestToken` transition.
      takeGuestSessionExpired();
      notify.info(t("guest.expired"), { id: "guest-expired" });
      return;
    }
    if (code === "guest_claimed") {
      // This conversation was claimed in another tab of this browser — also
      // the 403 a continued thread that moved into the account now gets
      // instead of a 404 (contract amendment B-2). It lives in an account, so
      // the guest is over here: forget the pointer and re-bootstrap. Signed
      // in elsewhere → the member chat on that thread with the text as the
      // composer seed (HomeRoute's own member redirect lands on the same URL).
      // No member session (claimed, then signed out) → a new guest chat with
      // the text back, and a toast that offers to sign in to that account.
      popPendingUserTurn(text);
      pendingFailedTextRef.current = null;
      resetStreaming();
      const claimedThread = openThreadId;
      const threadPath =
        claimedThread !== null
          ? `/chat?${THREAD_PARAM}=${encodeURIComponent(claimedThread)}`
          : "/chat";
      clearGuestPointer();
      // Still a guest in THIS browser (no member session claimed it): a new
      // guest chat with the text back, and a toast offering to sign in to
      // the account that owns the conversation now. Shared by the
      // "not refreshed" outcome AND a rejected rebootstrap (P12) — a
      // rejection must never be read as "a member session appeared".
      const settleAsGuest = (): void => {
        handleNewChat();
        if (text !== "") composerRef.current?.prefill(text);
        notify.info(t("guest.gate.titleClaimed"), {
          id: "guest-claimed",
          description: t("guest.gate.bodyClaimed"),
          action: {
            label: t("guest.gate.login"),
            onClick: () => {
              // The sanitised link every sign-in entry builds (final web fix
              // round, item 10).
              navigate(withReturnTo("/login", threadPath));
            },
          },
        });
      };
      // Fix round 1, item 4d: `.then(onFulfilled, onRejected)` on the SAME
      // call, not `.then(onFulfilled).catch(onRejected)` — the chained form
      // would route an exception thrown INSIDE `onFulfilled` itself (e.g.
      // from `navigate`) to `onRejected` too, mistaking it for "rebootstrap
      // failed" and re-settling as a guest after already deciding to
      // navigate as a member. `onRejected` here only ever sees an actual
      // rejection of `rebootstrapAfterGuestEnded()` — never one of its own
      // success branch — and (P12) must never become an unhandled rejection
      // or be read as "a member session appeared": treated exactly like
      // "not refreshed".
      // Fix round 2, item 4: a terminal `.catch` as well — `settleAsGuest`
      // (called from either branch above) or `navigate` could still throw,
      // and `.then(onFulfilled, onRejected)` alone does not catch an
      // exception thrown INSIDE either of its own handlers. Swallowed
      // deliberately: there is nothing further to recover to here, and the
      // one thing that must never happen is this becoming an unhandled
      // promise rejection.
      void rebootstrapAfterGuestEnded()
        .then(
          (outcome) => {
            if (outcome === "refreshed") {
              navigate(threadPath, { replace: true, ...(text !== "" ? { state: { seed: text } } : {}) });
              return;
            }
            settleAsGuest();
          },
          () => {
            settleAsGuest();
          },
        )
        .catch(() => {});
      return;
    }
    // Transient (unavailable, IP cap, attachments refused): the bubble stays
    // as the retry anchor and the error slot words it.
    const slotError: ChatStreamError = { code };
    if (e.retryAfterSeconds !== undefined) slotError.retryAfterSeconds = e.retryAfterSeconds;
    setStreamError(slotError);
    if (code === "guest_attachments_locked") setGate({ reason: "attachments" });
  };

  // A `clarify_answer` that failed before its first token (W3). The summary
  // bubble goes — it was never the start of an answer. A round someone else
  // closed (409 or the in-stream `clarify_round_closed`, contract C7) stays
  // closed and the thread is re-read. Anything else reopened the round
  // server-side (spec "Answer turn" step 5), so the card re-docks with the
  // draft intact for a retry: a guest refusal is then handled exactly like a
  // refused send (the sign-up card, a new chat or the claimed re-bootstrap),
  // and the rest gets the usual toast.
  // A `clarify_answer` that failed AFTER its first token (re-review ruling):
  // the server saves nothing on such an end and reopens the round (M4), so
  // the summary bubble goes — it was never stored — and the round reopens,
  // locally and in the cached thread. The card re-docks with the draft
  // intact as the retry; the error slot keeps no resend.
  clarifyAnswerBrokeRef.current = (submit): void => {
    popPendingUserTurn(submit.summary);
    setRoundStatus(submit.roundId, "open");
  };

  clarifySubmitErrorRef.current = (e, submit): void => {
    popPendingUserTurn(submit.summary);
    resetStreaming();
    if (e.code === "clarify_round_closed") {
      const threadId = activeThreadId ?? serverThreadIdRef.current;
      if (threadId !== null) {
        void queryClient.invalidateQueries({ queryKey: threadQueryKey(threadId) });
      }
      return;
    }
    setRoundStatus(submit.roundId, "open");
    if (
      guest &&
      isGuestStreamErrorCode(e.code) &&
      (stopCauseFor(e.code, e.guestReason ?? null) !== null ||
        e.code === "guest_session_expired" ||
        e.code === "guest_claimed")
    ) {
      guestRefusalRef.current(e);
      return;
    }
    toastForErrorRef.current(e);
  };

  // Guest cold load: `/` with no `?t=` reopens the conversation the pointer
  // remembers (the transcript comes back from the server through the cookie).
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!guest || restoredRef.current) return;
    restoredRef.current = true;
    const threadId = readGuestPointer()?.threadId ?? null;
    if (urlThreadId !== null || activeThreadId !== null || threadId === null) return;
    if (useAuthStore.getState().guestToken === null) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(THREAD_PARAM, threadId);
        return next;
      },
      { replace: true },
    );
    // Mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A guest without a session cannot read any thread: a stale `?t=` (shared
  // link, expired or discarded session) becomes a new chat instead of a send
  // into a thread this visitor does not own. Three guards narrow "stale":
  //
  // `isStreaming` (fix round 1, item 1): `endGuestSessionLocally` (inside
  // `attemptGuestRefresh`, run by the stream's own 401 handling) clears
  // `guestToken` EAGERLY, before `onError`/`guestRefusalRef` ever runs — so
  // there is a real intermediate render, mid-send, with `guestToken === null`
  // for a REOPENED thread (`?t=` on load; never adopted, so `localThreadId`
  // stays null). Without this guard THIS effect read that render as "stale"
  // and called `handleNewChat` first, which nulled `activeThreadId` and —
  // via the `[activeThreadId]` effect above — `pendingFailedTextRef`, so by
  // the time `guestRefusalRef`'s own `guest_session_expired` branch finally
  // ran it read an EMPTY text and silently dropped what the guest typed. The
  // send's own handlers own this transition end to end; this effect must
  // stay out of the way until they are done (`isStreaming` goes false via
  // `resetStreaming`/`stopStreaming`/`handleNewChat` itself, all called from
  // those handlers).
  //
  // `isMember` guards a SEPARATE race, reachable only when `<ChatRoute>` is
  // mounted bare (exactly how this file's own tests render it): the
  // `guest_claimed` → rebootstrap path ALSO clears `guestToken` — once the
  // member refresh has answered (residual R1: no longer eagerly, so a
  // reopened thread's `?t=` survives the wait), right after a "refreshed"
  // member token — and by then `isStreaming` is already false
  // (`resetStreaming` ran first in that branch), so `isMember` is what stops
  // this effect from reacting to that clear and stripping the `?t=` the
  // rebootstrap's own `navigate("/chat?t=<id>")` had just restored. Under the real app
  // (HomeRoute) this guard is belt-and-suspenders: a member token unmounts
  // the guest `<ChatRoute>` in favour of the member one before this effect
  // would ever see it, so the race it guards against cannot reach this file
  // at all there — it only matters for a composition like this file's tests.
  //
  // `pointer?.hasSession` (fix round 1, item 3): a cold boot whose member
  // refresh came back `"unavailable"` (offline/5xx — no guest refresh is
  // even attempted then) or a guest refresh itself returning 503
  // `guest.unavailable` (contract B-3: the session is KEPT) both leave
  // `guestToken === null` while the pointer still says the session is alive.
  // Without this guard "no token in memory yet" read as "no session at all"
  // and erased the pointer's own thread out from under a session that was
  // never actually over — the thread query simply stays disabled until a
  // guest token exists, and the next send's `ensureGuestSession` resumes the
  // SAME guest through the cookie. The user's own «Новый чат»
  // (`handleNewChat`) still clears the pointer's thread unconditionally —
  // only THIS "is it stale" read is softened.
  useEffect(() => {
    if (!guest || !bootstrapped || isMember || isStreaming || guestToken !== null) {
      return;
    }
    if (urlThreadId === null || urlThreadId === localThreadId) return;
    if (pointer?.hasSession === true) return;
    handleNewChat();
  }, [
    guest,
    bootstrapped,
    isMember,
    isStreaming,
    guestToken,
    urlThreadId,
    localThreadId,
    pointer,
    handleNewChat,
  ]);

  // "The earlier chat expired" — once, when the guest refresh found it gone.
  useEffect(() => {
    if (guest && takeGuestSessionExpired()) {
      notify.info(t("guest.expired"), { id: "guest-expired" });
    }
  }, [guest, guestToken, t]);

  // The pointer forgets a thread the server will not serve to this guest.
  //
  // Fix round 2, item 3 (probe P-H): when a send is PENDING into that same
  // thread — `pendingFailedTextRef` still holds the text because the send
  // never resolved before the thread turned not-found (the thread was
  // claimed/pruned/abandoned between opening it and sending, or the stream
  // itself 404s for it) — a plain pointer-clear is not enough.
  // `threadError === "notFound"` makes MessageList replace its WHOLE log
  // with the generic not-found slot (checked before the in-flight/error-slot
  // branch), which would otherwise hide the user's own just-pushed bubble
  // AND whatever the stream itself reported, with no visible way out other
  // than the slot's own "New chat" button — losing the typed text. Recovered
  // exactly like `guest_session_expired`: pop the bubble, keep the text,
  // start a new chat (which clears the pointer's thread as part of its own
  // logic, so the plain clear below is skipped for this case). With nothing
  // pending, the existing behaviour is unchanged: only the pointer forgets
  // the thread, and the not-found slot's own "New chat" button
  // (`onThreadErrorNewChat`) is how the user leaves it.
  //
  // W2.6 (W2.4c carry b): the recovery ABANDONS that send — `abortSend()`
  // cancels its transport, and an aborted send dispatches nothing more
  // (useChatStream). `resetStreaming`/`stopStreaming` alone only reset the
  // UI state, so its late answer used to land under the fresh chat: a 404
  // as a provider_error slot and toast, a 403 `guest.claimed` in the
  // claimed branch with an empty text (the seed already went back here).
  useEffect(() => {
    if (!guest || threadError !== "notFound" || openThreadId === null) return;
    const pendingText = pendingFailedTextRef.current;
    if (pendingText !== null) {
      abortSend();
      popPendingUserTurn(pendingText);
      pendingFailedTextRef.current = null;
      resetStreaming();
      handleNewChat();
      composerRef.current?.prefill(pendingText);
      return;
    }
    const current = readGuestPointer();
    if (current !== null && current.threadId === openThreadId) {
      writeGuestPointer({ ...current, threadId: null });
    }
  }, [guest, threadError, openThreadId, abortSend, popPendingUserTurn, resetStreaming, handleNewChat]);

  // W2.6 — the visitor's unsent guest text comes back in the member's
  // composer once nothing is pending: after a claim (a fresh intent or an
  // explicit «Сохранить» — the same person), or when a guest typed and then
  // signed in without ever sending. «Не сохранять», logout and a session end
  // discard it first (shared devices). Cleared only once it is back in the
  // box, so a composer that is not there yet cannot lose it.
  useEffect(() => {
    if (guest || claimPending) return;
    const draft = readGuestDraft();
    const composer = composerRef.current;
    if (draft === null || composer === null) return;
    clearGuestDraft();
    composer.prefill(draft);
  }, [guest, claimPending]);

  // W2.6 (ruling P10) — a claim that found the guest session gone opened the
  // pointer's thread: normally a sequential re-claim after a successful one
  // (B-4), and the thread loads. If the session was pruned instead, the
  // thread went with it and lands on the not-found state — whose slot then
  // reads "this guest chat expired" (`openThreadIsGone` → MessageList), not
  // a deletion the member never made. That slot is the one message (ruling
  // P12: no toast on top). The record is dropped once the member's own fetch
  // of the thread succeeds — not on a cached copy still being refetched —
  // and with the session (`resetGuestClaim`).
  const openThreadIsGone = !guest && goneThreadId !== null && openThreadId === goneThreadId;
  const goneThreadLoaded = threadQuery.isSuccess && !threadQuery.isFetching;
  useEffect(() => {
    if (openThreadIsGone && goneThreadLoaded) clearGoneThread();
  }, [openThreadIsGone, goneThreadLoaded]);

  // Locate the MessageList scroll container (owned by MessageList, found by its
  // data-testid) so we can cache + restore the per-thread offset without
  // touching MessageList (outside this story's allowed-touch list).
  const findScrollEl = useCallback((): HTMLElement | null => {
    const root = messageAreaRef.current;
    return (
      root?.querySelector<HTMLElement>('[data-testid="chat-message-list"]') ??
      null
    );
  }, []);

  // Commit the active thread switch: cache the outgoing thread's scroll offset,
  // then set the new active thread (AC#5). The restore happens in a layout
  // effect once the new thread's messages have rendered.
  const commitSwitch = useCallback(
    (threadId: string): void => {
      if (activeThreadId !== null) {
        const el = findScrollEl();
        if (el) setScrollPosition(activeThreadId, el.scrollTop);
      }
      stopStreaming();
      setActiveThreadId(threadId);
    },
    [
      activeThreadId,
      findScrollEl,
      setActiveThreadId,
      setScrollPosition,
      stopStreaming,
    ],
  );

  // Select a thread. Mid-stream, prompt to confirm aborting the stream (AC#14);
  // otherwise switch immediately.
  const handleSelectThread = useCallback(
    (threadId: string): void => {
      if (threadId === activeThreadId) return;
      if (isStreaming) {
        setPendingSwitchId(threadId);
        return;
      }
      commitSwitch(threadId);
    },
    [activeThreadId, commitSwitch, isStreaming],
  );

  // Restore the new thread's cached scroll offset after its messages render
  // (AC#5). Runs when the active thread or its loaded messages change.
  useLayoutEffect(() => {
    if (activeThreadId === null) return;
    const offset = scrollPositions[activeThreadId];
    if (offset === undefined) return;
    const el = findScrollEl();
    if (el) el.scrollTop = offset;
    // Re-run when the messages for the active thread finish loading.
  }, [activeThreadId, messages.length, scrollPositions, findScrollEl]);

  return (
    <div
      className="flex h-full w-full bg-[var(--chat-ground)]"
      data-testid="chat-route"
      data-guest={guest ? "true" : undefined}
      data-guest-gate={gate?.reason}
      data-guest-stop={guestStopCause ?? undefined}
    >
      {/*
        The imported design's desktop frame (spec §4.2/§4.3). At ≥1200px in
        light theme it wraps the rail and the conversation in the design's
        ground; anywhere else it renders them as the plain siblings they have
        always been — the composition below 1200px is untouched by this.
      */}
      <AssistantFrame
        sidebar={
          guestConfig !== undefined ? (
            <GuestHistoryPanel
              onNewChat={handleNewChat}
              onSignIn={openHistoryGate}
              mobileOpen={historyOpen}
              onMobileOpenChange={setHistoryOpen}
            />
          ) : (
            <ThreadList
              activeThreadId={activeThreadId}
              onNewChat={handleNewChat}
              onSelectThread={handleSelectThread}
              mobileOpen={historyOpen}
              onMobileOpenChange={setHistoryOpen}
            />
          )
        }
      >
        <main
          ref={messageAreaRef}
          className="relative isolate flex min-w-0 flex-1 flex-col overflow-hidden"
        >
          {/* No backdrop: the chat ground is solid `--chat-ground` (white in
            light mode) with no shyrdak motif — owner, 2026-10-15. */}
          <header className="flex items-center gap-2 border-b border-[var(--g-rule)] bg-[var(--g-surface)] px-3 py-2 md:px-4 md:py-2.5">
            <button
              type="button"
              aria-label={guest ? t("guest.locked.historyAria") : t("sidebar.open")}
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen(true)}
              data-testid="chat-sidebar-toggle"
              className="-ml-1 flex size-10 shrink-0 items-center justify-center rounded-[10px] text-[var(--g-ink-2)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] md:hidden"
            >
              <PanelLeft {...iconProps("md")} />
            </button>
            <h1 className="min-w-0 flex-1 truncate font-display text-[18px] font-extrabold tracking-[-0.03em] text-foreground">
              {t("title")}
            </h1>
          </header>

          {/* C1 — the space above the empty conversation (see `centreEmpty`). */}
          {centreEmpty ? <div aria-hidden="true" className="flex-1" /> : null}

          {claimPending ? (
            // W2.6 — the member hold: nothing of the guest conversation shows
            // (shared devices) until the person decides, and nothing can be
            // sent into it as this member.
            <GuestSavePrompt
              phase={claimPhase}
              pointer={pointer}
              onSave={saveGuestClaim}
              onDontSave={handleDontSaveGuestClaim}
            />
          ) : (
            <>
              <MessageList
                messages={messages}
                localTurns={localTurns}
                isStreaming={isStreaming}
                routingIntent={routingIntent}
                currentAssistantText={currentAssistantText}
                currentProductCards={currentProductCards}
                currentSellerCards={currentSellerCards}
                currentCitations={currentCitations}
                currentFollowups={currentFollowups}
                currentStage={currentStage}
                awaitingFirstFrameSince={awaitingFirstFrameSince}
                currentMaterials={currentMaterials}
                currentPlan={currentPlan}
                currentClarify={currentClarify}
                answerAnchorRoundId={answerAnchorRoundId}
                streamError={streamError}
                onPickStarter={handlePickStarter}
                onOpenQuoteUpload={handleOpenQuoteUpload}
                threadPending={threadPending}
                threadError={threadError}
                threadNotFoundReason={openThreadIsGone ? "guest-expired" : undefined}
                onRetryThread={handleRetryThread}
                onThreadErrorNewChat={handleNewChat}
                // B3 — at the ceiling (with `has_more` still true) the control
                // hides instead of offering a no-op click that would 422.
                hasEarlierMessages={
                  (transcript?.has_more ?? false) &&
                  messageLimit < THREAD_MESSAGE_MAX_LIMIT
                }
                loadingEarlier={threadQuery.isFetching}
                onLoadEarlier={handleLoadEarlier}
                onRegenerate={handlePickStarter}
                streamErrorRetryable={primaryTurn !== "clarify_answer"}
                exploredNeedIds={exploredNeedIdSet}
                needLoading={needLoadingSet}
                needErrored={needErroredSet}
                onFetchNeed={handleFetchNeed}
                onSeeAllNeed={handleSeeAllNeed}
                desktop={isDesktop}
                centred={centreEmpty}
                {...(guest ? { guest: { attachmentsLocked } } : {})}
              />

              {dockedRound !== null ? (
                // Clarifying-question round (spec "Frontend"): the card takes
                // the composer's slot. At the message wall it wins over the
                // sign-up card, which returns once the round is answered.
                // The «Свой ответ» cap rides the round itself (round.custom_max_chars,
                // index amendment A3) — no prop to pass.
                <ClarifyCard
                  key={dockedRound.round_id}
                  round={dockedRound}
                  draft={draftFor(dockedRound, clarifyDraft)}
                  onDraftChange={setClarifyDraft}
                  disabled={anyNeedLoading}
                  onSubmit={(answers) => handleClarifySubmit(dockedRound, answers, false)}
                  onSkip={() => handleClarifySkip(dockedRound)}
                  onDismiss={() => handleClarifyDismiss(dockedRound)}
                  onPickOption={(option) => handleClarifyPick(dockedRound, option)}
                />
              ) : guestConfig !== undefined && guestStopCause !== null && !answerStreaming ? (
                // SCRUM-274 — the stop causes (limit/tokens/budget/disabled)
                // replace the composer with the sign-up card; the transcript
                // above stays readable and its product links keep working.
                // A clarifying-question answer still streaming keeps the
                // composer (and its Stop) until `done` (W3.2 fix round).
                <GuestGateCard
                  cause={guestStopCause}
                  allowance={guestAllowance}
                  returnTo={guestReturn}
                  focusOnMount={guestGateCardFocusOnMount || gateCardFocusAfterAnswer}
                />
              ) : (
                <MessageInput
                  ref={composerRef}
                  isStreaming={isStreaming}
                  onSend={handleSend}
                  onStop={handleStop}
                  seedText={seedText}
                  {...(guestConfig !== undefined
                    ? {
                        guest: {
                          attachmentsLocked,
                          onLockedAttach: openAttachmentsGate,
                          voice: {
                            voiceEnabled: guestConfig.voice_enabled,
                            languages: guestConfig.voice_languages,
                            maxDurationSeconds: guestConfig.voice_max_duration_seconds,
                            onSignInRequired: openVoiceGate,
                          },
                          // SCRUM-274 — privacy + retention small print, and the
                          // optional "N left" hint (contract: warn-only).
                          footer: (
                            <GuestComposerNotice
                              retentionDays={guestConfig.retention_days}
                              remaining={remainingHint}
                            />
                          ),
                        },
                      }
                    : {})}
                />
              )}
            </>
          )}

          {/* C1 — …and the matching space below it. */}
          {centreEmpty ? <div aria-hidden="true" className="flex-1" /> : null}
        </main>
      </AssistantFrame>

      {/* Mid-stream switch confirm (AC#14): aborts the live stream cleanly. */}
      {pendingSwitchId !== null && (
        <SwitchStreamConfirm
          onConfirm={() => {
            const target = pendingSwitchId;
            setPendingSwitchId(null);
            commitSwitch(target);
          }}
          onCancel={() => setPendingSwitchId(null)}
        />
      )}

      {/* Smart Recommendations R1 task 4 — "See all →" full category sheet. */}
      <NeedSeeAllSheet
        need={seeAllNeed}
        onClose={() => setSeeAllNeed(null)}
        desktop={isDesktop}
      />

      {/* SCRUM-274 — the sign-up gate: limit / attachments / history / voice. */}
      {guestConfig !== undefined ? (
        <GuestGateSheet
          gate={gate}
          allowance={guestAllowance}
          returnTo={guestReturn}
          onClose={() => setGate(null)}
          onPasteInstead={() => {
            setGate(null);
            // Deferred past BottomSheet's own close effect (which restores
            // focus to whatever opened the sheet — the locked clip button,
            // for the attachments gate): `prependLeadIn` focuses the
            // textarea, and that focus call must be the LAST one to run, or
            // the close effect's restore overwrites it a moment later (fix
            // round, item 2). The lead-in goes in front of what the guest
            // already typed (final web fix round, item 7).
            requestAnimationFrame(() => {
              composerRef.current?.prependLeadIn(t("guest.pastePrefix"));
            });
          }}
          desktop={isDesktop}
        />
      ) : null}
    </div>
  );
}

/**
 * Confirm prompt shown when the user switches threads mid-stream (AC#14).
 * Self-contained accessible modal (no shadcn Dialog primitive available);
 * focus the safe default (cancel) and dismiss on Escape.
 */
function SwitchStreamConfirm({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation("chat");
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid="chat-switch-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("switch.title")}
        data-testid="chat-switch-dialog"
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-lg"
      >
        <h2 className="font-display text-[19px]/[1.556] font-bold tracking-tight text-foreground">
          {t("switch.title")}
        </h2>
        <p className="mt-1.5 text-[15px]/[1.429] text-muted-foreground">
          {t("switch.body")}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button
            ref={cancelRef}
            type="button"
            variant="secondary"
            onClick={onCancel}
            data-testid="chat-switch-cancel"
          >
            {t("switch.cancel")}
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={onConfirm}
            data-testid="chat-switch-confirm"
          >
            {t("switch.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Re-export so the error-mapping type stays co-located if needed. */
export type { ApiError };

export default ChatRoute;
